import { useEffect, useState } from "react";
import { BellRing, Cake, CalendarClock, HandHeart, Loader2, MessageCircle, Play, Send, Users, Church } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const TOGGLES = [
  ["reminder_event", "Pengingat Event H-1", CalendarClock, "Dikirim ke semua peserta terdaftar sehari sebelum event/kegiatan."],
  ["reminder_service", "Pengingat Ibadah H-1", Church, "Dikirim ke semua jemaat sehari sebelum ibadah mingguan terjadwal."],
  ["reminder_community", "Pengingat RC & TTS H-1", Users, "Dikirim ke peserta pertemuan komunitas (RC/TTS) sehari sebelumnya."],
  ["reminder_ministry", "Pengingat Jadwal Pelayanan H-1", HandHeart, "Dikirim ke pelayan yang bertugas besok."],
  ["reminder_birthday", "Ucapan Ulang Tahun", Cake, "Dikirim otomatis & terjadwal pukul 00:00 WITA di hari ulang tahun jemaat."],
];
const TEMPLATES = [
  ["template_event", "Template Event H-1", "{nama}, {event}, {waktu}, {lokasi}"],
  ["template_service", "Template Ibadah H-1", "{nama}, {ibadah}, {waktu}, {lokasi}"],
  ["template_community", "Template RC & TTS H-1", "{nama}, {komunitas}, {waktu}, {lokasi}"],
  ["template_ministry", "Template Jadwal Pelayanan", "{nama}, {divisi}, {peran}, {ibadah}, {waktu}"],
  ["template_birthday", "Template Ulang Tahun", "{nama}"],
];
const KIND_LABEL = { event_h1: "Event H-1", service_h1: "Ibadah H-1", community_meeting_h1: "RC/TTS H-1", ministry_h1: "Jadwal Pelayanan", birthday: "Ulang Tahun" };

export default function WhatsAppGateway() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [logs, setLogs] = useState([]);
  const [test, setTest] = useState({ target: "", message: "Tes notifikasi WhatsApp dari GIM Church Makassar." });
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/notify/settings").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadLogs = () => api.get("/admin/notify/logs?limit=30").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { load(); loadLogs(); }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/admin/notify/settings", { ...cfg, ...(token ? { token } : {}) });
      setCfg(data);
      setToken("");
      toast.success("Pengaturan WhatsApp disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const sendTest = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/notify/test-wa", test);
      toast[data.sent ? "success" : "error"](data.sent ? "Pesan tes terkirim" : `Gagal: ${data.status || data.detail || "cek token"}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const runNow = async () => {
    setBusy("run");
    try {
      await api.post("/admin/notify/run-reminders");
      toast.success("Pengingat harian dijalankan");
      loadLogs();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!cfg) return <Skeleton className="h-96" />;
  return (
    <div data-testid="admin-whatsapp">
      <AdminTitle eyebrow="Gateway" title="WhatsApp & Reminder">
        <button onClick={save} disabled={busy === "save"} className={btnPrimary} data-testid="wa-save-button">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pengaturan"}</button>
      </AdminTitle>

      <div className="grid lg:grid-cols-[26rem_1fr] gap-6">
        <div className="space-y-6 self-start">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-3" data-testid="wa-config-card">
            <p className="font-extrabold text-slate-900 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-500" />Koneksi API — {cfg.provider}</p>
            <p className="text-xs text-slate-500">Dapatkan token di dashboard Fonnte → menu Device → Token. Nomor pengirim harus sudah tersambung di Fonnte.</p>
            <label className="block">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">API Token</span>
              <input className={`${inputCls} mt-1.5`} placeholder={cfg.token_masked || "Tempel token Fonnte"} value={token} onChange={(e) => setToken(e.target.value)} data-testid="wa-token-input" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nomor Pengirim (opsional)</span>
              <input className={`${inputCls} mt-1.5`} placeholder="628xxxxxxxxxx" value={cfg.sender || ""} onChange={(e) => setCfg({ ...cfg, sender: e.target.value })} data-testid="wa-sender-input" />
            </label>
            <button onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })} data-testid="wa-enable-toggle"
              className={`w-full rounded-xl py-3 text-sm font-bold transition ${cfg.enabled ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>
              {cfg.enabled ? "Gateway AKTIF" : "Gateway nonaktif"}
            </button>
            <p className="text-[11px] text-slate-400">Status token: {cfg.configured ? `tersimpan (${cfg.token_masked})` : "belum diisi"} · {cfg.push_subscriptions} perangkat berlangganan push.</p>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-3" data-testid="wa-test-card">
            <p className="font-extrabold text-slate-900">Uji Kirim</p>
            <input className={inputCls} placeholder="Nomor tujuan (08xx / 62xx)" value={test.target} onChange={(e) => setTest({ ...test, target: e.target.value })} data-testid="wa-test-target" />
            <textarea rows={3} className={inputCls} value={test.message} onChange={(e) => setTest({ ...test, message: e.target.value })} data-testid="wa-test-message" />
            <button onClick={sendTest} disabled={busy === "test" || test.target.length < 8} className={`${btnPrimary} w-full !py-2.5`} data-testid="wa-test-button">{busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" />Kirim Tes</>}</button>
            <button onClick={runNow} disabled={busy === "run"} className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl py-2.5 text-sm font-bold" data-testid="wa-run-reminders">{busy === "run" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4" />Jalankan Pengingat Sekarang</>}</button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="wa-reminders-card">
            <p className="font-extrabold text-slate-900 flex items-center gap-2"><BellRing className="w-4 h-4 text-blue-500" />Auto Reminder</p>
            <p className="text-xs text-slate-500 mt-1">Pengingat H-1 berjalan otomatis setiap hari pukul 06:30 WITA; ucapan ulang tahun pukul 00:00 WITA (in-app + push + WhatsApp).</p>
            <div className="mt-4 space-y-3">
              {TOGGLES.map(([key, label, Icon, desc]) => (
                <div key={key} className="flex items-start gap-3 rounded-2xl border border-slate-100 p-4" data-testid={`wa-toggle-row-${key}`}>
                  <span className="w-10 h-10 shrink-0 rounded-xl bg-blue-50 text-blue-600 grid place-items-center"><Icon className="w-5 h-5" /></span>
                  <span className="flex-1 min-w-0"><span className="block font-bold text-sm text-slate-900">{label}</span><span className="block text-xs text-slate-500">{desc}</span></span>
                  <button onClick={() => setCfg({ ...cfg, [key]: !cfg[key] })} data-testid={`wa-toggle-${key}`}
                    className={`shrink-0 text-[11px] font-bold rounded-full px-3 py-1.5 ${cfg[key] ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>{cfg[key] ? "Aktif" : "Nonaktif"}</button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-4" data-testid="wa-templates-card">
            <p className="font-extrabold text-slate-900">Template Pesan</p>
            {TEMPLATES.map(([key, label, vars]) => (
              <label key={key} className="block">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
                <textarea rows={3} className={`${inputCls} mt-1.5`} value={cfg[key] || ""} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} data-testid={`wa-template-${key}`} />
                <span className="text-[11px] text-slate-400">Variabel: {vars}</span>
              </label>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="wa-logs-card">
            <p className="font-extrabold text-slate-900">Log Pengiriman Terakhir</p>
            {logs.length === 0 ? <p className="mt-3 text-sm text-slate-500" data-testid="wa-logs-empty">Belum ada pengingat terkirim.</p> : (
              <div className="mt-3 divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {logs.map((l) => (
                  <div key={l.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`wa-log-${l.id}`}>
                    <span className="min-w-0"><span className="block font-bold text-slate-900 truncate">{KIND_LABEL[l.kind] || l.kind}{l.title ? ` · ${l.title}` : ""}</span><span className="block text-[11px] text-slate-400">{l.user_id || "-"}</span></span>
                    <span className="shrink-0 text-[11px] text-slate-500">{fmtDate(l.created_at, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
